import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Icon } from "@/components/layout/Icon";
import { cn } from "@/lib/utils/cn";
import { can, useAuthStore } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";
import { KPI_TONE_CLASSES, greeting } from "./sections-utils";
import { employeeDisplayName, employeeDisplayNameWithCode, formatMoneyCompact, formatPeriod, titleCase } from "@/lib/utils/format";
import type { Department, Employee, LeaveRequest, MonthlySummary } from "@/types/domain";
import { HiringTrendChart } from "./HiringTrendChart";
import { DepartmentDonut } from "./DepartmentDonut";
import { BranchDonut } from "./BranchDonut";
import { AttendanceDonut } from "./AttendanceDonut";

// The presentational sections of the tenant dashboard. They live here so the
// default dashboard (DashboardPage) and the customizable dashboard's widgets
// (layout/widgets) render the very same markup - one definition per section,
// no second copy to drift. Every component here takes its data as props; the
// callers own the queries and the permission/feature gates.

/** The dashboard's greeting and quick actions. Shared by the default
 * dashboard and a customized one so both open the same way; `extraActions`
 * adds buttons (the customized dashboard's "Customize"). */
export function DashboardHeader({ period, extraActions }: { period?: string; extraActions?: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const employeeMgmtEnabled = useFeatureEnabled("employee_management");
  const leaveEnabled = useFeatureEnabled("leave_management");
  const payrollEnabled = useFeatureEnabled("payroll_management");
  const pill =
    "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors duration-200 ease-out hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700";
  return (
    <>
      {/* Hero greeting - the dashboard's own moment, not the shared
          PageHeader every other page uses - this is the one screen allowed
          a bespoke identity treatment. */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-slate-900">
            {greeting()}, <span className="italic">{user?.first_name ?? ""}</span>
          </h1>
          {period && <p className="mt-1 text-sm text-slate-500">Here's what's happening this {formatPeriod(period)}.</p>}
        </div>
        {((employeeMgmtEnabled && can("employee.create")) || leaveEnabled || (payrollEnabled && can("payroll.read")) || extraActions) && (
          <div className="flex flex-wrap items-center gap-2">
            {employeeMgmtEnabled && can("employee.create") && (
              <Link to="/employees/new" className={pill}>
                <Icon name="plus" className="h-3.5 w-3.5" />
                Add Employee
              </Link>
            )}
            {leaveEnabled && (
              <Link to="/leaves" className={pill}>
                <Icon name="calendar-check" className="h-3.5 w-3.5" />
                Apply for Leave
              </Link>
            )}
            {payrollEnabled && can("payroll.read") && (
              <Link to="/payroll/runs" className={pill}>
                <Icon name="wallet" className="h-3.5 w-3.5" />
                View Payroll
              </Link>
            )}
            {extraActions}
          </div>
        )}
      </div>
    </>
  );
}

export function PulseStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      <p className="text-xs font-medium text-slate-600">{label}</p>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/** One reference-style KPI card: icon in a soft colored circle, an optional
 * trend pill (only rendered when real period-over-period data exists - see
 * computeTrend), the headline number, and an optional real mini-sparkline
 * (only rendered when a real data series exists, e.g. Expense's
 * monthly_trend - never a decorative fake chart). */
export function KpiCard({
  label,
  value,
  hint,
  icon,
  tone,
  trend,
  sparkline,
  to,
}: {
  to?: string;
  label: string;
  value: ReactNode;
  hint?: string;
  icon: string;
  tone: keyof typeof KPI_TONE_CLASSES;
  trend?: { value: string; positive: boolean };
  sparkline?: number[];
}) {
  const maxSpark = sparkline && sparkline.length > 0 ? Math.max(1, ...sparkline) : 0;
  const card = (
    <Card interactive glow={tone === "accent" ? "accent" : "primary"}>
      <CardBody>
        <div className="flex items-start justify-between">
          <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl2", KPI_TONE_CLASSES[tone])}>
            <Icon name={icon} className="h-5 w-5" />
          </div>
          {trend && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold",
                trend.positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700",
              )}
            >
              {trend.positive ? "▲" : "▼"} {trend.value}
            </span>
          )}
          {!trend && sparkline && sparkline.length > 1 && (
            <div className="flex h-6 items-end gap-0.5">
              {sparkline.map((v, i) => (
                <div key={i} className="w-1.5 rounded-sm bg-accent-200" style={{ height: `${Math.max(15, (v / maxSpark) * 100)}%` }} />
              ))}
            </div>
          )}
        </div>
        <p className="mt-3 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          {hint && <p className="text-[11px] text-slate-400">· {hint}</p>}
        </div>
      </CardBody>
    </Card>
  );
  // Every KPI opens the list it summarises - no dead cards.
  return to ? (
    <Link to={to} aria-label={`${label}: open`} className="block rounded-xl2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600">
      {card}
    </Link>
  ) : (
    card
  );
}

// ---------------------------------------------------------------------------
// Workforce insights - the tabbed Hiring Trend / Departments / Attendance /
// Branches chart card.
// ---------------------------------------------------------------------------

export interface InsightsTab {
  key: string;
  label: string;
}

export function WorkforceInsightsCard({
  tabs,
  hiringTrend,
  departments,
  departmentSummary,
  attendanceMix,
  branches,
  className,
}: {
  tabs: InsightsTab[];
  hiringTrend: { label: string; hires: number }[];
  departments: Department[];
  departmentSummary?: { manager_count: number; departments_without_head: number };
  attendanceMix: Record<string, number>;
  branches: Parameters<typeof BranchDonut>[0]["branches"];
  className?: string;
}) {
  const [insightsTab, setInsightsTab] = useState("hiring");
  const activeInsightsTab = tabs.some((t) => t.key === insightsTab) ? insightsTab : tabs[0]?.key;
  return (
    <Card interactive className={className}>
      <div className="flex items-center gap-5 border-b border-slate-100 px-5 pt-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setInsightsTab(t.key)}
            className={cn(
              "-mb-px border-b-2 pb-3 text-sm font-medium transition-colors",
              activeInsightsTab === t.key ? "border-primary-700 text-primary-800" : "border-transparent text-slate-500 hover:text-slate-700",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <CardBody>
        {activeInsightsTab === "hiring" && <HiringTrendChart data={hiringTrend} />}
        {activeInsightsTab === "departments" && (
          <>
            <DepartmentDonut departments={departments} />
            {departmentSummary && (
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <span>
                  <span className="font-semibold text-slate-800">{departmentSummary.manager_count}</span> managers
                </span>
                <span>
                  <span className={cn("font-semibold", departmentSummary.departments_without_head > 0 ? "text-amber-600" : "text-slate-800")}>
                    {departmentSummary.departments_without_head}
                  </span>{" "}
                  department{departmentSummary.departments_without_head === 1 ? "" : "s"} without a head
                </span>
              </div>
            )}
          </>
        )}
        {activeInsightsTab === "attendance" && <AttendanceDonut mix={attendanceMix} />}
        {activeInsightsTab === "branches" && <BranchDonut branches={branches} />}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Leave Applications
// ---------------------------------------------------------------------------

export function LeaveApplicationsCard({ items, className }: { items?: LeaveRequest[]; className?: string }) {
  return (
    <Card interactive className={className}>
      <CardHeader>
        <CardTitle>Leave Applications</CardTitle>
        <Link to="/leaves" className="text-xs font-medium text-primary-700 hover:underline">
          View all →
        </Link>
      </CardHeader>
      <CardBody className="p-0">
        {!items || items.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar-check" className="h-7 w-7" />}
            title="No leave requests yet"
            description="Applications will appear here once submitted."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((leave) => {
              const name = employeeDisplayName(leave.employee);
              return (
                <li key={leave.id} className="flex items-center gap-3 px-5 py-3 transition-colors duration-200 hover:bg-primary-50/40">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-800">
                    {name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">{employeeDisplayNameWithCode(leave.employee)}</p>
                    <p className="truncate text-xs capitalize text-slate-500">{leave.type.replace(/_/g, " ")}</p>
                  </div>
                  <StatusBadge status={leave.status} />
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Employee Status (by-status headcount) and Employee List
// ---------------------------------------------------------------------------

const STATUS_COLORS = ["#6c3fd4", "#ea4c89", "#8b5cf6", "#0d9488", "#b45309", "#6e6d7a"];

export function EmployeeStatusCard({ byStatus, className }: { byStatus?: Record<string, number | undefined>; className?: string }) {
  // The reference dashboard groups by employment type with invented
  // percentages; this app has only a real employee-*status* aggregate (the
  // employees/headcount endpoint), so that is what's shown.
  const statusEntries = Object.entries(byStatus ?? {}).filter(([, v]) => (v ?? 0) > 0) as [string, number][];
  const statusTotal = statusEntries.reduce((sum, [, v]) => sum + v, 0);
  return (
    <Card interactive className={className}>
      <CardHeader>
        <CardTitle>Employee Status</CardTitle>
      </CardHeader>
      <CardBody>
        {statusEntries.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No data yet</p>
        ) : (
          <>
            <p className="mb-4 text-3xl font-semibold tabular-nums text-slate-900">
              {statusTotal} <span className="text-sm font-medium text-slate-500">Total Employees</span>
            </p>
            <div className="space-y-3">
              {statusEntries.map(([status, count], i) => (
                <div key={status}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium capitalize text-slate-700">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }} />
                      {status.replace(/_/g, " ")}
                    </span>
                    <span className="text-slate-500">
                      {count} · {((count / statusTotal) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(count / statusTotal) * 100}%`, backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export function EmployeeListCard({ items, className }: { items?: Employee[]; className?: string }) {
  return (
    <Card interactive className={className}>
      <CardHeader>
        <CardTitle>Employee List</CardTitle>
        <Link to="/employees" className="text-xs font-medium text-primary-700 hover:underline">
          View all →
        </Link>
      </CardHeader>
      <CardBody className="p-0">
        {!items || items.length === 0 ? (
          <EmptyState icon={<Icon name="users" className="h-7 w-7" />} title="No employees yet" description="Add your first employee to get started." />
        ) : (
          <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Name</th>
                  <th className="px-5 py-2 font-medium">Designation</th>
                  <th className="px-5 py-2 font-medium">Department</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.slice(0, 6).map((emp) => (
                  <tr key={emp.id} className="transition-colors duration-200 hover:bg-primary-50/40">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-800">
                          {emp.full_name.slice(0, 1).toUpperCase()}
                        </div>
                        <span className="truncate font-medium text-slate-800">{employeeDisplayNameWithCode(emp)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-slate-600">{emp.designation || "—"}</td>
                    <td className="px-5 py-2.5 text-slate-600">{emp.department?.name || "—"}</td>
                    <td className="px-5 py-2.5">
                      <StatusBadge status={emp.status} />
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <Link
                        to={`/employees/${emp.id}`}
                        className="inline-flex rounded-lg p-1 text-slate-400 transition-all duration-200 ease-out hover:scale-105 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <Icon name="eye" className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Compact secondary metrics, Payroll snapshot and Geo-Fencing snapshot
// ---------------------------------------------------------------------------

export function PulseCard({ stats }: { stats: { key: string; label: string; value: string; hint?: string }[] }) {
  return (
    <Card interactive>
      <CardBody className="flex flex-wrap items-center gap-x-8 gap-y-3">
        {stats.map((s) => (
          <PulseStat key={s.key} label={s.label} value={s.value} hint={s.hint} />
        ))}
      </CardBody>
    </Card>
  );
}

export function PayrollSnapshotCard({ payroll, period }: { payroll: NonNullable<MonthlySummary["payroll"]>; period: string }) {
  return (
    <Card interactive>
      <CardHeader>
        <CardTitle>Payroll — {formatPeriod(period)}</CardTitle>
        <Badge tone={payroll.status === "locked" || payroll.status === "paid" || payroll.status === "completed" ? "indigo" : "amber"}>
          {titleCase(payroll.status)}
        </Badge>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Employees paid</span>
          <span className="text-sm font-medium text-slate-800">{payroll.employee_count}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Net payout</span>
          <span className="text-sm font-semibold text-slate-900">{formatMoneyCompact(payroll.total_net)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Cost to company</span>
          <span className="text-sm font-medium text-slate-800">{formatMoneyCompact(payroll.total_ctc)}</span>
        </div>
        <Link to="/payroll/runs" className="mt-1 text-center text-xs font-medium text-primary-700 hover:underline">
          View payroll runs →
        </Link>
      </CardBody>
    </Card>
  );
}

export function GeoSnapshotCard({
  snapshot,
  interactive,
}: {
  snapshot: { active_geo_fences: number; branches_covered: number; verified_today: number };
  interactive?: boolean;
}) {
  return (
    <Card interactive={interactive}>
      <CardHeader>
        <CardTitle>Geo-Fencing</CardTitle>
        <Link to="/geo-fencing" className="text-xs font-medium text-primary-700 hover:underline">
          Manage →
        </Link>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Active fences</span>
          <span className="text-sm font-medium text-slate-800">{snapshot.active_geo_fences}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Branches covered</span>
          <span className="text-sm font-medium text-slate-800">{snapshot.branches_covered}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Verified today</span>
          <span className="text-sm font-semibold text-emerald-600">{snapshot.verified_today}</span>
        </div>
      </CardBody>
    </Card>
  );
}
